import React, { useState } from 'react';
import { CheckCircle2, AlertCircle, Lightbulb, ArrowRight, ChevronDown, ChevronUp, Sparkles, TrendingUp, Target } from 'lucide-react';

const AssessmentAnalysisRefined = () => {
  const [expandedSections, setExpandedSections] = useState({
    insight1: true,
    insight2: false,
    insight3: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="min-h-screen bg-stone-50 font-['Reddit_Sans',system-ui,sans-serif]">
      {/* Header - Anchor Block */}
      <div className="bg-slate-900 text-white px-8 py-6 border-b-2 border-slate-900">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="flex flex-col gap-1 mt-1">
                <div className="w-2 h-2 rounded-full bg-coral-400"></div>
                <div className="w-2 h-2 rounded-full bg-gold-400"></div>
                <div className="w-2 h-2 rounded-full bg-teal-400"></div>
              </div>
              <div>
                <h1 className="text-xl font-bold mb-1">Assessment Response Analysis</h1>
                <p className="text-sm text-slate-300">Your results, key truths, and deep dive</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold flex items-center gap-2">
                85%
                <Sparkles size={20} className="text-gold-400" />
              </div>
              <div className="text-xs text-slate-400">Ready to build</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-8">
        {/* Key Insight Card - White with strong border */}
        <div className="bg-white border-2 border-slate-900 rounded-xl p-6 mb-6 relative overflow-hidden">
          {/* Small gradient accent bar - warm to cool spectrum */}
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-coral-400 via-yellow-500 to-teal-400"></div>
          
          <div className="flex items-start gap-3 mb-4">
            <div className="flex flex-col gap-1.5">
           
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide">Key Insight</h2>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                You're not getting material help—let's surface the decision-points and "through-lines" in the strategy practically written itself.
              </p>
            </div>
          </div>
        </div>

        {/* What Makes You Stand Out */}
        <div className="bg-white border border-slate-900 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-base font-bold text-slate-900">What Makes You Stand Out</h2>
          </div>
          
          {/* Insight Section 1 */}
          <div className="mb-4">
            <button
              onClick={() => toggleSection('insight1')}
              className="w-full flex items-center justify-between text-left py-2 border-b border-stone-200 hover:border-gold-300 transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-gold-500"></div>
                <span className="text-sm font-bold text-slate-900">Your Distinctive Pattern</span>
                {expandedSections.insight1}
              </div>
              {expandedSections.insight1 ? <ChevronUp size={16} className="text-slate-600" /> : <ChevronDown size={16} className="text-slate-400" />}
            </button>
            {expandedSections.insight1 && (
              <div className="pt-4 pb-2 text-sm text-slate-700 leading-relaxed  pl-4 ml-1">
                <p className="mb-3">
                  Here's what I found: Unlike your brain-works-you-don't just see problems, you see the invisible systems underneath them. Like when everyone was focused on the dissection itself, you were thinking about why an old teaching method still depends on learning tools when it should have evolved.
                </p>
                <p>
                  You have this thing where you automatically zoom out to ask "what's broken here?" and build connections that cause, not just the symptom.
                </p>
              </div>
            )}
          </div>

          {/* Insight Section 2 */}
          <div className="mb-4">
            <button
              onClick={() => toggleSection('insight2')}
              className="w-full flex items-center justify-between text-left py-2 border-b border-stone-200 hover:border-coral-300 transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-coral-500"></div>
                <span className="text-sm font-bold text-slate-900">The Story I Want to Know More About</span>
                {expandedSections.insight2 }
              </div>
              {expandedSections.insight2 ? <ChevronUp size={16} className="text-slate-600" /> : <ChevronDown size={16} className="text-slate-400" />}
            </button>
            {expandedSections.insight2 && (
              <div className="pt-4 pb-2 text-sm text-slate-700 leading-relaxed pl-4 ml-1">
                Your student government funding restructure story—moving from popularity-based to impact-inclusion based promotion.
              </div>
            )}
          </div>

          {/* Insight Section 3 */}
          <div className="mb-4">
            <button
              onClick={() => toggleSection('insight3')}
              className="w-full flex items-center justify-between text-left py-2 border-b border-stone-200 hover:border-azure-300 transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-azure-500"></div>
                <span className="text-sm font-bold text-slate-900">Here's What I Can Do With More Details</span>
                {expandedSections.insight3 }
              </div>
              {expandedSections.insight3 ? <ChevronUp size={16} className="text-slate-600" /> : <ChevronDown size={16} className="text-slate-400" />}
            </button>
            {expandedSections.insight3 && (
              <div className="pt-4 pb-2 text-sm text-slate-700 leading-relaxed  pl-4 ml-1">
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <div className="w-1 h-1 rounded-full bg-azure-500 mt-1.5 flex-shrink-0"></div>
                    <span>Once you add the specific 10+ stories about I can help you:</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1 h-1 rounded-full bg-azure-500 mt-1.5 flex-shrink-0"></div>
                    <span>Find events, threads that get free-you, agencies systems change through student leadership</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1 h-1 rounded-full bg-azure-500 mt-1.5 flex-shrink-0"></div>
                    <span>Figure out how to talk about your dorm damage political impacts without sounding preachy</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1 h-1 rounded-full bg-azure-500 mt-1.5 flex-shrink-0"></div>
                    <span>Connect what you actually do in the funding restructure to policy programs you'd want to study</span>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Action Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-coral-50 border-l-4 border-l-coral-600 rounded-lg p-4 relative">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb size={14} className="text-coral-700" />
              <h3 className="text-xs font-bold text-coral-900 uppercase tracking-wide">Highlight Your Impact</h3>
            </div>
            <p className="text-xs text-coral-800 leading-relaxed">
              Don't overthink—list core visible activities. Explain impact. Your transformation will shape how adcoms read concrete successes.
            </p>
          </div>

          <div className="bg-gold-50 border-l-4 border-l-gold-600 rounded-lg p-4 relative">
            <div className="flex items-center gap-2 mb-2">
              <Target size={14} className="text-gold-700" />
              <h3 className="text-xs font-bold text-gold-900 uppercase tracking-wide">Give Me More to Work With</h3>
            </div>
            <p className="text-xs text-gold-800 leading-relaxed">
              Expand the trade-offs you weighed and the key decision you made. Your dorm damage political impact is fascinating, but I need more substance.
            </p>
          </div>

          <div className="bg-teal-50 border-l-4 border-l-teal-600 rounded-lg p-4 relative">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={14} className="text-teal-700" />
              <h3 className="text-xs font-bold text-teal-900 uppercase tracking-wide">Explain How You Got Here</h3>
            </div>
            <p className="text-xs text-teal-800 leading-relaxed">
              Add the short backstory or turning point. "How I became your team leader, how you read your education class?"
            </p>
          </div>
        </div>

        {/* Response Analysis Section */}
        <div className="bg-white border border-slate-300 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <h2 className="text-base font-bold text-slate-900">Response Analysis</h2>
          </div>
          <p className="text-xs text-stone-600 mb-6">Here's what information is missing from your key responses</p>

          {/* Student Government Response Card */}
          <div className="border border-slate-300 rounded-lg mb-4 overflow-hidden">
            <div className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
              <span className="text-sm font-bold">Student Government Funding Restructure</span>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-gold-400"></div>
                <span className="text-xs bg-slate-700 px-2 py-1 rounded">Strong</span>
              </div>
            </div>
            <div className="p-4">
              <p className="text-xs text-slate-700 italic mb-4 border-l-2 border-stone-300 pl-3">
                "When I ran for Student Body President, I proposed that we restructure how student government allocates funding to clubs—moving from a popularity-based model to one based on impact, inclusion, and need..."
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-gold-50 border-l-2 border-l-gold-500 rounded p-3 relative">
                 
                  <div className="flex items-start gap-2 mb-1">
                    <Lightbulb size={14} className="text-gold-600 mt-0.5 flex-shrink-0" />
                    <span className="text-xs font-bold text-gold-900">What I See</span>
                  </div>
                  <p className="text-xs text-gold-800 leading-relaxed">
                    This shows your system-thinking and coalition-building skills brilliantly. You see what support for change.
                  </p>
                </div>

                <div className="bg-coral-50 border-l-2 border-l-coral-500 rounded p-3 relative">
                 
                  <div className="flex items-start gap-2 mb-1">
                    <AlertCircle size={14} className="text-coral-600 mt-0.5 flex-shrink-0" />
                    <span className="text-xs font-bold text-coral-900">What I Need to Know</span>
                  </div>
                  <p className="text-xs text-coral-800 leading-relaxed">
                    Give me more detail: what specific criteria did which specific clubs benefit? How many students did you ultimately reach?
                  </p>
                </div>

                <div className="bg-teal-50 border-l-2 border-l-teal-500 rounded p-3 relative">
                  
                  <div className="flex items-start gap-2 mb-1">
                    <CheckCircle2 size={14} className="text-teal-600 mt-0.5 flex-shrink-0" />
                    <span className="text-xs font-bold text-teal-900">Help Me Understand</span>
                  </div>
                  <p className="text-xs text-teal-800 leading-relaxed">
                    Walk me through what you actually discussed originally, when it moved from bureaucratic, and can you share if it's had any success story?
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mb-8">
          <button className="flex-1 bg-coral-500 text-white border-2 border-slate-900 rounded-lg px-6 py-3 text-sm font-bold hover:bg-coral-600 transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2 relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-r from-coral-500 to-coral-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <span className="relative flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
              Open Assessment and Add More Details
              <ArrowRight size={16} />
            </span>
          </button>
          <button className="flex-1 bg-teal-600 text-white border-2 border-slate-900 rounded-lg px-6 py-3 text-sm font-bold hover:bg-teal-700 transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2 relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-r from-teal-600 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <span className="relative flex items-center gap-2">
              <Sparkles size={16} />
              Generate Strategic Essay Topics
              <ArrowRight size={16} />
            </span>
          </button>
        </div>

        {/* Bottom Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-300 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-sm font-bold text-slate-900">How This Analysis Helps You</h3>
            </div>
            <ul className="space-y-2 text-xs text-slate-700">
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-coral-500 mt-1.5 flex-shrink-0"></div>
                <span>Identifies your unique strengths and compelling storylines</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-coral-500 mt-1.5 flex-shrink-0"></div>
                <span>Reveals gaps in your narrative where added details make a difference</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-coral-500 mt-1.5 flex-shrink-0"></div>
                <span>Generates personalized essay topics when quality matches is high</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-coral-500 mt-1.5 flex-shrink-0"></div>
                <span>Creates strategic insight for college positioning</span>
              </li>
            </ul>
          </div>

          <div className="bg-white border border-slate-300 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-sm font-bold text-slate-900">Response Quality Goals</h3>
            </div>
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-700">Basic Insight</span>
                  </div>
                  <span className="text-xs font-bold text-slate-900">40%+</span>
                </div>
                <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-coral-400 to-coral-500" style={{width: '40%'}}></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-700">Detailed Analysis</span>
                  </div>
                  <span className="text-xs font-bold text-slate-900">70%+</span>
                </div>
                <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-gold-400 to-gold-500" style={{width: '70%'}}></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-700">Essay Topic Generation</span>
                  </div>
                  <span className="text-xs font-bold text-slate-900">70%+</span>
                </div>
                <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-teal-400 to-teal-500" style={{width: '70%'}}></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-700">Strategic Positioning</span>
                  </div>
                  <span className="text-xs font-bold text-slate-900">80%+</span>
                </div>
                <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-azure-400 to-azure-600" style={{width: '85%'}}></div>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2 mt-3 pt-3 border-t border-stone-200">
              <p className="text-xs text-stone-600">
                *The more details you provide, the more personalized and strategic becomes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentAnalysisRefined;